import React, { useMemo, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { fullName, money } from '../lib/data';
import { useWorkflow, useWorkflows, useWorkflowAction, useRunEligibility } from '../api/hooks';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AgentChip from '../components/AgentChip';
import Confidence from '../components/Confidence';
import StatusPill from '../components/StatusPill';
import RiskBadge from '../components/RiskBadge';
import Pipeline from '../components/Pipeline';
import {
  ArrowLeft, MessageSquare, Mail, Smartphone, ShieldCheck, Send, Bot, Paperclip,
  Check, RefreshCw, Sparkles, User, ArrowUpRight,
} from 'lucide-react';
import { toast } from '../lib/aiToast';

const CHANNELS = [
  { key: 'sms', label: 'SMS', icon: Smartphone },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'portal', label: 'Secure portal', icon: ShieldCheck },
];

const emailFor = (p) => `${p?.firstName?.toLowerCase()}.${p?.lastName?.toLowerCase()}@example.com`;

const RERUN_STAGES = ['Reply received', '270 re-sent', '271 received', 'Parsed', 'Re-scored'];

export default function CommThread() {
  const { id } = useParams();
  const nav = useNavigate();
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';

  // The workflow detail (with embedded patient/payer) by route id; fall back to
  // the first open communication item when navigated without a valid id.
  const { data: detail } = useWorkflow(id, practiceId);
  const { data: commsItems = [] } = useWorkflows(practiceId, { queue: 'communication' });
  const item = detail || commsItems[0] || {};
  const pat = item.patient;
  const payer = item.payer;

  const send2 = useWorkflowAction();
  const runEligibility = useRunEligibility();

  const isCoverageIssue = /inactive|coverage/i.test(`${item.title || ''}${item.agentSummary || ''}`);

  const TEMPLATES = useMemo(() => ([
    { key: 'default', label: isCoverageIssue ? 'Insurance update request' : 'Good-faith estimate notice', text: item.draft || '' },
    { key: 'reminder', label: 'Appointment reminder + copay', text: `Hi ${pat?.firstName}, this is a reminder of your appointment on May 5 with ${payer?.name ? 'your provider' : 'us'}. Your estimated copay is ${money(30)}. Reply STOP to opt out. — Pacific Dermatology Group` },
    { key: 'callback', label: 'Request a callback', text: `Hi ${pat?.firstName}, we need to confirm a few insurance details before your visit on May 5. Please call us at (415) 555-0100 at your convenience. — Pacific Dermatology Group` },
  ]), [item, pat, payer, isCoverageIssue]);

  const [channel, setChannel] = useState('sms');
  const [templateKey, setTemplateKey] = useState('default');
  const [draft, setDraft] = useState('');
  // Seed the draft from the loaded item once it arrives, before the user composes.
  const seeded = useRef(false);
  React.useEffect(() => {
    if (!seeded.current && item.id) {
      setDraft(item.draft || TEMPLATES[0].text);
      seeded.current = true;
    }
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [messages, setMessages] = useState([]); // {dir, channel, text, ts, attachment?}
  const [phase, setPhase] = useState('compose'); // compose | awaiting_reply | replied | rerunning | resolved
  const [rerunStage, setRerunStage] = useState(-1);
  const [result, setResult] = useState(null);
  const [ocr, setOcr] = useState(null);
  const timers = useRef([]);

  const pickTemplate = (k) => { setTemplateKey(k); setDraft(TEMPLATES.find((t) => t.key === k)?.text || ''); };

  const send = () => {
    if (!draft.trim()) return;
    const ChannelLabel = CHANNELS.find((c) => c.key === channel)?.label;
    setMessages((m) => [...m, { dir: 'out', channel, text: draft, ts: 'now' }]);
    setPhase('awaiting_reply');
    toast.success(`Sent via ${ChannelLabel} to ${fullName(pat)}`);
    // Persist the approval to the backend; the scripted reply below drives the demo.
    if (item.id) send2.mutateAsync({ id: item.id, action: 'send', note: draft }).catch(() => {});

    // Simulated patient reply
    timers.current.push(setTimeout(() => {
      const reply = isCoverageIssue
        ? { dir: 'in', channel, ts: 'just now', text: `Hi! Yes I switched jobs. New plan is Blue Cross Blue Shield, member ID XEK884120301. Card attached.`, attachment: 'insurance_card.jpg' }
        : { dir: 'in', channel, ts: 'just now', text: `Thanks for letting me know. I understand the ~${money(480)} estimate and can put a card on file at check-in.` };
      setMessages((m) => [...m, reply]);
      setPhase('replied');
      toast.message('Patient replied');
      if (isCoverageIssue) {
        timers.current.push(setTimeout(() => {
          setOcr({ memberId: 'XEK884120301', payer: 'Anthem Blue Cross Blue Shield', group: '00630554', confidence: 0.93 });
          toast.success('Data Sync Agent read the insurance card');
        }, 900));
      }
    }, 2200));
  };

  const rerun = () => {
    setPhase('rerunning');
    setRerunStage(0);
    // Kick the real re-run if we have a patient; the scripted stages below render the result.
    if (item.patientId) runEligibility.mutateAsync({ patientId: item.patientId, appointmentId: item.appointmentId }).catch(() => {});
    timers.current.forEach(clearTimeout);
    RERUN_STAGES.forEach((_, i) => timers.current.push(setTimeout(() => setRerunStage(i + 1), 420 * (i + 1))));
    timers.current.push(setTimeout(() => {
      setResult({
        planNameReturned: 'Anthem Blue PPO', networkStatusResolved: 'in_network', active: true, level: 'clear',
        copay: 30, deductibleInd: 1000, deductibleIndMet: 1000, coinsurance: 0.1,
        summary: `Updated coverage verified: active Anthem Blue PPO, in-network for this practice. Deductible met. Copay ${money(30)}. Patient cleared for check-in.`,
      });
      setPhase('resolved');
      toast.success('Coverage re-verified — appointment cleared');
    }, 420 * RERUN_STAGES.length + 250));
  };

  const resolveAck = () => { setPhase('resolved'); toast.success('Marked resolved — patient acknowledged'); };

  const rerunSteps = RERUN_STAGES.map((label, i) => ({ label, status: rerunStage > i ? 'done' : rerunStage === i ? 'active' : 'pending' }));
  const ChannelIcon = CHANNELS.find((c) => c.key === channel)?.icon || MessageSquare;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/app"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">{fullName(pat)}</h1>
            <span className="pill pill-indigo">Patient comms</span>
            <StatusPill status={phase === 'resolved' ? 'sent' : 'awaiting_review'} label={phase === 'resolved' ? 'Resolved' : 'Open'} />
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{item.title}</div>
        </div>
        <span className="text-xs text-slate-400 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" />{pat?.phone}</span>
      </div>

      {/* Why this was raised */}
      <div className="ai-surface p-4">
        <div className="flex items-center gap-2 mb-1.5"><AgentChip agent="comms" size="xs" /><Confidence value={item.confidence} /></div>
        <p className="text-sm text-slate-700">{item.agentSummary}</p>
        <div className="mt-2 flex items-start gap-1.5 text-sm"><Bot className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" /><span className="text-slate-800"><span className="font-medium">Recommended:</span> {item.recommendation}</span></div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Conversation */}
        <div className="lg:col-span-3 dh-card-flush flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-brand-500" />Conversation</div>
            <span className="text-[11px] text-slate-400 flex items-center gap-1"><ChannelIcon className="h-3.5 w-3.5" />{CHANNELS.find((c) => c.key === channel)?.label}</span>
          </div>

          <div className="flex-1 p-4 space-y-3 min-h-[220px] bg-[#fafbfc]">
            {messages.length === 0 && (
              <div className="text-center text-sm text-slate-400 py-10">No messages yet. Review the drafted message and approve to send.</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.dir === 'out' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[78%] ${m.dir === 'out' ? 'order-2' : ''}`}>
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.dir === 'out' ? 'bg-brand-600 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'}`}>
                    {m.text}
                    {m.attachment && (
                      <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] rounded-lg px-2 py-1 ${m.dir === 'out' ? 'bg-white/15' : 'bg-slate-100 text-slate-600'}`}>
                        <Paperclip className="h-3 w-3" />{m.attachment}
                      </div>
                    )}
                  </div>
                  <div className={`mt-1 text-[10px] text-slate-400 flex items-center gap-1 ${m.dir === 'out' ? 'justify-end' : ''}`}>
                    {m.dir === 'out' ? <><Check className="h-3 w-3" />Sent · {m.ts}</> : <><User className="h-3 w-3" />{pat?.firstName} · {m.ts}</>}
                  </div>
                </div>
              </div>
            ))}

            {phase === 'awaiting_reply' && (
              <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5">
                <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-slate-300 dh-pulse" /><span className="h-1.5 w-1.5 rounded-full bg-slate-300 dh-pulse" style={{ animationDelay: '.2s' }} /><span className="h-1.5 w-1.5 rounded-full bg-slate-300 dh-pulse" style={{ animationDelay: '.4s' }} /></span>
              </div></div>
            )}
          </div>

          {/* Composer / next action */}
          <div className="border-t border-slate-100 p-3">
            {phase === 'compose' && (
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <div className="seg">
                    {CHANNELS.map((c) => { const Icon = c.icon; return <button key={c.key} className={channel === c.key ? 'active' : ''} onClick={() => setChannel(c.key)}><Icon className="h-3.5 w-3.5 inline mr-1" />{c.label}</button>; })}
                  </div>
                  <Select value={templateKey} onValueChange={pickTemplate}>
                    <SelectTrigger className="h-8 w-auto text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{TEMPLATES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="ml-auto"><AgentChip agent="comms" size="xs" /></span>
                </div>
                <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} className="text-sm" />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-400">{channel === 'email' ? emailFor(pat) : channel === 'sms' ? pat?.phone : 'Secure patient portal'}</span>
                  <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={send}><Send className="h-4 w-4 mr-1.5" />Approve & send</Button>
                </div>
              </div>
            )}
            {phase === 'awaiting_reply' && <div className="text-center text-xs text-slate-400 py-2">Waiting for patient response… eligibility will re-run automatically on reply.</div>}
            {phase === 'replied' && (
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-slate-600">{isCoverageIssue ? 'New insurance received.' : 'Patient acknowledged.'}</div>
                {isCoverageIssue
                  ? <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={rerun}><RefreshCw className="h-4 w-4 mr-1.5" />Re-run eligibility</Button>
                  : <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={resolveAck}><Check className="h-4 w-4 mr-1.5" />Mark resolved</Button>}
              </div>
            )}
            {(phase === 'rerunning' || phase === 'resolved') && (
              <div className="text-center text-xs text-emerald-600 py-2 flex items-center justify-center gap-1.5"><Check className="h-3.5 w-3.5" />Loop closed by Communication + Verification agents</div>
            )}
          </div>
        </div>

        {/* Side: loop status */}
        <div className="lg:col-span-2 space-y-4">
          <div className="dh-card p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">Communication loop</div>
            <p className="text-xs text-slate-500 mb-3">The agent drafts; you approve; the patient replies; eligibility re-runs — without anyone re-keying data.</p>
            <ol className="space-y-2.5">
              {[
                { k: 'draft', label: 'Agent drafted message', done: true },
                { k: 'sent', label: 'Human approved & sent', done: messages.some((m) => m.dir === 'out') },
                { k: 'reply', label: 'Patient replied', done: messages.some((m) => m.dir === 'in') },
                { k: 'rerun', label: isCoverageIssue ? 'Eligibility re-run' : 'Acknowledged', done: phase === 'resolved' },
                { k: 'resolved', label: 'Worklist item cleared', done: phase === 'resolved' },
              ].map((s) => (
                <li key={s.k} className="flex items-center gap-2.5 text-sm">
                  <span className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${s.done ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}>{s.done ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />}</span>
                  <span className={s.done ? 'text-slate-800' : 'text-slate-400'}>{s.label}</span>
                </li>
              ))}
            </ol>
          </div>

          {ocr && (
            <div className="dh-card p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Paperclip className="h-4 w-4 text-brand-500" />Insurance card · OCR</div>
                <Confidence value={ocr.confidence} label="read" />
              </div>
              <div className="flex items-center gap-2 mb-2"><AgentChip agent="sync" size="xs" /></div>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Payer</dt><dd className="text-slate-900 text-right">{ocr.payer}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Member ID</dt><dd className="text-slate-900 mono">{ocr.memberId}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Group</dt><dd className="text-slate-900 mono">{ocr.group}</dd></div>
              </dl>
              <div className="text-[11px] text-emerald-600 mt-2 flex items-center gap-1"><Check className="h-3 w-3" />Auto-filled into the eligibility re-run</div>
            </div>
          )}

          {(phase === 'rerunning' || phase === 'resolved') && (
            <div className="dh-card p-5">
              <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><RefreshCw className="h-4 w-4 text-brand-500" />Eligibility re-run</div>
              <div className="dh-inset p-3"><Pipeline steps={rerunSteps} /></div>
              {result && (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div><div className="text-xs text-slate-500">New plan</div><div className="font-semibold text-slate-900 text-sm">{result.planNameReturned}</div></div>
                    <div className="flex flex-col items-end gap-1"><StatusPill status="in_network" /><RiskBadge level="clear" /></div>
                  </div>
                  <div className="ai-surface p-3 text-sm text-slate-800"><div className="flex items-center gap-2 mb-1"><AgentChip agent="verification" size="xs" /></div>{result.summary}</div>
                  <Link to="/app"><Button variant="outline" className="w-full"><ArrowUpRight className="h-4 w-4 mr-1.5" />Back to worklist</Button></Link>
                </div>
              )}
            </div>
          )}

          {phase === 'resolved' && (
            <div className="dh-card p-4 bg-emerald-50/50 border-emerald-100">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-800"><Sparkles className="h-4 w-4" />Resolved without re-keying</div>
              <div className="text-xs text-emerald-700 mt-1">{isCoverageIssue ? 'Coverage updated from the patient’s reply and re-verified automatically.' : 'Patient acknowledged the estimate; flagged to collect at check-in.'}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
